import React from "react";

interface StatsError {
    user_id: number;
    currency: string;
    wallet_balance: number;
    stats_total: number;
    difference: number;
}

interface StatsIntegrityData {
    total_users: number;
    processed_users: number;
    errors_count: number;
    is_complete: boolean;
    errors: StatsError[];
}

interface StatsIntegrityCheckProps {
    statsIntegrityData: { data: StatsIntegrityData } | null | undefined;
    isVerifyingStats: boolean;
    statsVerifyProgress: number;
    onVerifyStatsIntegrity: () => void;
    onFixUser: (userId: number) => void;
    onOpenDetails: (userId: number) => void;
}

export const StatsIntegrityCheck: React.FC<StatsIntegrityCheckProps> = ({
    statsIntegrityData,
    isVerifyingStats,
    statsVerifyProgress,
    onVerifyStatsIntegrity,
    onFixUser,
    onOpenDetails,
}) => {
    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Проверка соответствия статистики
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Сверка баланса кошелька с последней записью в статистике
                (stats.total)
            </p>

            <button
                onClick={onVerifyStatsIntegrity}
                disabled={isVerifyingStats}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-md disabled:opacity-50 mb-4"
            >
                {isVerifyingStats ? "Проверка..." : "Запустить проверку"}
            </button>

            {/* Прогресс-индикатор */}
            {isVerifyingStats && (
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">
                            Проверка соответствия статистики...{" "}
                            {statsVerifyProgress}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div
                            className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${statsVerifyProgress}%` }}
                        ></div>
                    </div>
                </div>
            )}

            {/* Результаты проверки */}
            {statsIntegrityData && (
                <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Всего пользователей
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {statsIntegrityData.data.total_users}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Обработано
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {statsIntegrityData.data.processed_users}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Ошибок найдено
                                </p>
                                <p
                                    className={`text-2xl font-bold ${
                                        statsIntegrityData.data.errors_count > 0
                                            ? "text-red-600 dark:text-red-400"
                                            : "text-green-600 dark:text-green-400"
                                    }`}
                                >
                                    {statsIntegrityData.data.errors_count}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Статус
                                </p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {statsIntegrityData.data.is_complete
                                        ? "Завершено"
                                        : "В процессе"}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Список ошибок */}
                    {statsIntegrityData.data.errors.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                                Найденные расхождения:
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                User ID
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Валюта
                                            </th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Баланс кошелька
                                            </th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Последняя stats
                                            </th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Разница
                                            </th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Действия
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {statsIntegrityData.data.errors.map(
                                            (
                                                error: StatsError,
                                                idx: number
                                            ) => (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                        {error.user_id}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {error.currency}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                        {error.wallet_balance.toFixed(
                                                            2
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                        {error.stats_total.toFixed(
                                                            2
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400 font-semibold">
                                                        {error.difference > 0
                                                            ? "+"
                                                            : ""}
                                                        {error.difference.toFixed(
                                                            2
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right flex gap-2 justify-end">
                                                        <button
                                                            title="Посмотреть детали"
                                                            onClick={() =>
                                                                onOpenDetails(
                                                                    error.user_id
                                                                )
                                                            }
                                                            className="text-blue-600 hover:text-blue-800"
                                                        >
                                                            🔍
                                                        </button>
                                                        <button
                                                            title="Исправить"
                                                            onClick={() =>
                                                                onFixUser(
                                                                    error.user_id
                                                                )
                                                            }
                                                            className="text-green-600 hover:text-green-800"
                                                        >
                                                            🛠️
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {statsIntegrityData.data.errors.length === 0 && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                            <p className="text-green-800 dark:text-green-200 font-medium">
                                ✓ Расхождений не найдено. Все данные корректны!
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
